import {
  BackupStatus,
  ConditionOperatorEnum,
  type Condition,
} from '@repo/api-gw-sdk';

import { BACKUP_STATUS_PROPERTY } from '../queryBuilder/propertyNames';

export const NON_POSTURE_BACKUP_STATUSES: BackupStatus[] = [
  BackupStatus.ExcludedFromBackup,
  BackupStatus.Disconnected,
  BackupStatus.Unsupported,
  BackupStatus.LimitExceeded,
];

/**
 * The statuses the dashboard's server-side aggregates leave out. A slice link has to
 * re-apply this list, otherwise the inventory it opens is longer than the slice it
 * came from. Classification failed and access lost are here but not above: the
 * inventory shows both without a toggle, the dashboard just doesn't count them —
 * neither has a BackedBy bucket to be counted into.
 */
export const DASHBOARD_EXCLUDED_BACKUP_STATUSES: BackupStatus[] = [
  ...NON_POSTURE_BACKUP_STATUSES,
  BackupStatus.ClassificationFailed,
  BackupStatus.AccessLost,
];

/**
 * The hidden statuses the inventory's filter bar carries as an ordinary filter.
 * Unsupported is missing on purpose: the inventory settings toggle owns it, so it
 * is excluded by the view rather than offered as a status to filter by.
 */
export const FILTER_BAR_EXCLUDED_BACKUP_STATUSES: BackupStatus[] =
  NON_POSTURE_BACKUP_STATUSES.filter(
    (status) => status !== BackupStatus.Unsupported
  );

export const createBackupStatusNotInCondition = (
  values: string[]
): Condition => ({
  property: BACKUP_STATUS_PROPERTY,
  operator: ConditionOperatorEnum.NotIn,
  value: values,
});

export const createBackupStatusInCondition = (values: string[]): Condition => ({
  property: BACKUP_STATUS_PROPERTY,
  operator: ConditionOperatorEnum.In,
  value: values,
});

/**
 * The statuses that make an on-demand backup request a no-op: the resource is gone,
 * unreachable, out of scope, or cannot be assessed. The backup API rejects every one of
 * them, so the console has to agree with it in all three places a backup can be started —
 * the single-resource action bar, the bulk menu's eligibility check, and the dialog's
 * request loop. Keeping one list is what stops a new status reaching only some of them.
 */
const UNBACKABLE_BACKUP_STATUSES: BackupStatus[] = [
  BackupStatus.Terminated,
  BackupStatus.Disconnected,
  BackupStatus.AccessLost,
  BackupStatus.Unsupported,
  BackupStatus.ClassificationFailed,
  BackupStatus.LimitExceeded,
];

export const isUnbackableStatus = (status: BackupStatus | undefined): boolean =>
  status !== undefined && UNBACKABLE_BACKUP_STATUSES.includes(status);
